/**
 * Rewrites image URLs from `customer-assets.emergentagent.com` to a proxy
 * endpoint on our own backend (`/api/img?url=…`).
 *
 * Why: extensions like uBlock Origin, Brave Shields, DNS filters (Pi-hole,
 * NextDNS, ISP), and corporate VPNs frequently block `emergentagent.com`
 * outright. By proxying through bethetrain.com's own origin, the photos
 * become same-origin and impossible to block without breaking the whole site.
 *
 * The backend `/api/img` endpoint streams the asset bytes with year-long
 * immutable cache headers and keeps a small in-memory LRU.
 */

const PROXY_HOSTS = ['customer-assets.emergentagent.com', 'assets.emergent.sh'];

/**
 * Proxy an asset URL through our backend (same-origin to bypass ad-blockers).
 *
 * @param {string} url - original asset URL
 * @param {object} [opts]
 * @param {number} [opts.w] - target CSS width in px. The backend resizes to
 *                            ~2× for retina and re-encodes to WebP. Cuts
 *                            large originals from 1-3 MB → 60-150 KB.
 * @param {number} [opts.q] - WebP quality (default 80 server-side).
 */
export const proxyImg = (url, opts = {}) => {
  if (!url || typeof url !== 'string') return url;
  try {
    const u = new URL(url);
    if (!PROXY_HOSTS.includes(u.host)) return url;
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const params = [`url=${encodeURIComponent(url)}`];
    if (opts.w) params.push(`w=${Math.max(1, Math.floor(opts.w))}`);
    if (opts.q) params.push(`q=${Math.max(40, Math.min(95, Math.floor(opts.q)))}`);
    return `${base}/api/img?${params.join('&')}`;
  } catch {
    return url;
  }
};
